<template>
    <div class="py-12">
        <div class="flex mt-6 justify-between">
            <h1 class="text-2xl font-bold text-gray-800">
                {{ reportedDomain.domain }} Reports
            </h1>
            <div class="mr-6">
                <div
                    :class="[
                        'relative',
                        !hasDateRangeAccess && 'cursor-pointer opacity-50',
                    ]"
                    @click="!hasDateRangeAccess && checkAccessOrShowModal()"
                >
                    <DateRangePicker
                        v-model="selectedDateRange"
                        :disabled="!hasDateRangeAccess"
                        @update:model-value="handleDateRangeChange"
                    />
                    <div
                        v-if="!hasDateRangeAccess"
                        class="absolute inset-0 bg-transparent cursor-pointer"
                        title="Click for upgrade options - Scale plan required"
                    />
                </div>
            </div>
        </div>

        <CrumbsBar :domain="reportedDomain.domain" :source="null"></CrumbsBar>

        <!-- Upgrade Required Section -->
        <UpgradeRequired v-if="!hasReportsAccess" class="mt-12" />

        <!-- Main Content -->
        <div v-else>
            <div
                v-if="reports.total.pass === 0 && reports.total.fail === 0"
                class="mt-10 rounded-md bg-white shadow-card"
            >
                <div class="px-6">
                    <div class="flex flex-col sm:flex-row">
                        <svg
                            class="ml-6 mt-10 flex shrink-0 sm:ml-0"
                            width="38"
                            height="38"
                            viewBox="0 0 38 38"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path
                                d="M19 11V22M19 27H19.02M37 19C37 21.3638 36.5344 23.7044 35.6298 25.8883C34.7252 28.0722 33.3994 30.0565 31.7279 31.7279C30.0565 33.3994 28.0722 34.7252 25.8883 35.6298C23.7044 36.5344 21.3638 37 19 37C16.6362 37 14.2956 36.5344 12.1117 35.6298C9.92784 34.7252 7.94353 33.3994 6.27208 31.7279C4.60062 30.0565 3.27475 28.0722 2.37017 25.8883C1.46558 23.7044 1 21.3638 1 19C1 14.2261 2.89642 9.64773 6.27208 6.27208C9.64773 2.89642 14.2261 1 19 1C23.7739 1 28.3523 2.89642 31.7279 6.27208C35.1036 9.64773 37 14.2261 37 19Z"
                                stroke="#FFC000"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            />
                        </svg>

                        <div class="ml-6 mt-6 sm:mt-[46px]">
                            <p class="font-medium leading-normal text-gray-800">
                                {{ $t("vue.no_results_for_domain") }}
                            </p>
                            <div
                                class="mb-12 mt-3.5 max-w-[550px] text-sm leading-normal text-gray-500"
                            >
                                <p>{{ $t("vue.please_check_back_later") }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <template v-else>
                <div>
                    <StatsBar
                        :stats="{
                            pass: reports.total.pass,
                            fail: reports.total.fail,
                        }"
                        :stats-before="null"
                    ></StatsBar>
                </div>

                <StatsChart
                    v-if="reports.total.pass > 0 || reports.total.fail > 0"
                    :chart-data="reports.chartData"
                    :header="$t('vue.daily_email_reports')"
                ></StatsChart>

                <div class="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
                    <div class="rounded-xl border border-gray-200 bg-white p-6">
                        <h3 class="mb-6 font-medium text-gray-900">
                            {{ $t("vue.domain_insights") }}
                        </h3>
                        <div class="space-y-4">
                            <div
                                class="flex items-start gap-4 rounded-lg border border-green-100 bg-green-50 p-4"
                            >
                                <div
                                    class="mt-2 h-2 w-2 shrink-0 rounded-full bg-green-500"
                                ></div>
                                <div>
                                    <p class="mb-1 font-medium text-green-900">
                                        {{ $t("vue.strong_compliance") }}
                                    </p>
                                    <p class="text-sm text-green-800">
                                        {{
                                            calculateCompliance(
                                                reports.total.pass,
                                                reports.total.fail,
                                            )
                                        }}
                                        {{
                                            $t(
                                                "vue.dmarc_compliance_rate_message",
                                            )
                                        }}
                                    </p>
                                </div>
                            </div>
                            <div
                                class="flex items-start gap-4 rounded-lg border border-blue-100 bg-blue-50 p-4"
                            >
                                <div
                                    class="mt-2 h-2 w-2 shrink-0 rounded-full bg-blue-500"
                                ></div>
                                <div>
                                    <p class="mb-1 font-medium text-blue-900">
                                        {{ $t("vue.main_sender") }}
                                    </p>
                                    <p class="text-sm text-blue-800">
                                        <strong>{{ mainSender.source }}</strong>
                                        {{ $t("vue.service_accounts_for") }}
                                        {{
                                            Math.round(
                                                (mainSender.sum /
                                                    (reports.total.pass +
                                                        reports.total.fail)) *
                                                    1000,
                                            ) / 10
                                        }}% {{ $t("vue.of_your_email_volume") }}
                                    </p>
                                </div>
                            </div>
                            <div
                                v-if="mainWarning"
                                class="flex items-start gap-4 rounded-lg border border-red-100 bg-red-50 p-4"
                            >
                                <div
                                    class="mt-2 h-2 w-2 shrink-0 rounded-full bg-red-500"
                                ></div>
                                <div>
                                    <p class="mb-1 font-medium text-red-900">
                                        {{ $t("vue.security_alert") }}
                                    </p>
                                    <p class="text-sm text-red-800">
                                        <strong>{{
                                            mainWarning.source
                                        }}</strong>
                                        {{
                                            $t(
                                                "vue.sender_showing_low_compliance",
                                            )
                                        }}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="rounded-xl border border-gray-200 bg-white p-6">
                        <h3 class="mb-6 font-medium text-gray-900">
                            {{ $t("vue.security_status") }}
                        </h3>
                        <div class="space-y-4">
                            <div class="flex items-center justify-between py-2">
                                <span class="text-gray-600">{{
                                    $t("vue.dmarc_policy")
                                }}</span
                                ><span
                                    data-slot="badge"
                                    class="w-fit [&amp;>svg]:size-3 [&amp;>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive text-secondary-foreground [a&amp;]:hover:bg-secondary/90 inline-flex shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border border-transparent bg-secondary px-2 py-0.5 text-xs font-medium transition-[color,box-shadow] focus-visible:ring-[3px]"
                                    >{{ reportedDomain.dmarcPolicy }}</span
                                >
                            </div>
                            <div class="flex items-center justify-between py-2">
                                <span class="text-gray-600">{{
                                    $t("vue.spf_record")
                                }}</span
                                ><span
                                    data-slot="badge"
                                    class="w-fit [&amp;>svg]:size-3 [&amp;>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive [a&amp;]:hover:bg-primary/90 inline-flex shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border border-transparent bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 transition-[color,box-shadow] focus-visible:ring-[3px]"
                                    >{{ $t("vue.valid") }}</span
                                >
                            </div>
                            <div class="flex items-center justify-between py-2">
                                <span class="text-gray-600">{{
                                    $t("vue.dkim_signing")
                                }}</span
                                ><span
                                    data-slot="badge"
                                    class="w-fit [&amp;>svg]:size-3 [&amp;>svg]:pointer-events-none focus-visible:border-ring focus-visible:ring-ring/50 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive [a&amp;]:hover:bg-primary/90 inline-flex shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border border-transparent bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 transition-[color,box-shadow] focus-visible:ring-[3px]"
                                    >{{ $t("vue.active") }}</span
                                >
                            </div>
                            <div
                                class="hidden items-center justify-between py-2"
                            >
                                <span class="text-gray-600">{{
                                    $t("vue.last_report")
                                }}</span
                                ><span class="font-medium text-gray-900"
                                    >1 {{ $t("vue.hour_ago") }}</span
                                >
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    class="mt-5 overflow-hidden rounded-xl border border-gray-200 bg-white"
                >
                    <div class="border-b border-gray-100 px-6 py-5">
                        <h3 class="font-medium text-gray-900">
                            {{ $t("vue.sender_details") }}
                        </h3>
                        <p class="mt-1 text-sm text-gray-600">
                            {{ $t("vue.authentication_compliance_metrics") }}
                        </p>
                    </div>
                    <div class="overflow-x-auto">
                        <table class="w-full">
                            <thead class="border-b border-gray-100 bg-gray-50">
                                <tr>
                                    <th
                                        class="px-6 py-4 text-left text-sm font-medium text-gray-600"
                                    >
                                        {{ $t("vue.sender") }}
                                    </th>
                                    <th
                                        class="px-6 py-4 text-left text-sm font-medium text-gray-600"
                                    >
                                        {{ $t("vue.health") }}
                                    </th>
                                    <th
                                        class="px-6 py-4 text-left text-sm font-medium text-gray-600"
                                    >
                                        {{ $t("vue.emails_reported") }}
                                    </th>
                                    <th
                                        class="px-6 py-4 text-left text-sm font-medium text-gray-600"
                                    >
                                        {{ $t("vue.dmarc_compliance") }}
                                    </th>
                                    <th
                                        class="px-6 py-4 text-left text-sm font-medium text-gray-600"
                                    >
                                        {{ $t("vue.spf_aligned") }}
                                    </th>
                                    <th
                                        class="px-6 py-4 text-left text-sm font-medium text-gray-600"
                                    >
                                        {{ $t("vue.dkim_aligned") }}
                                    </th>
                                </tr>
                            </thead>
                            <tbody class="divide-y divide-gray-100">
                                <tr
                                    v-for="row in reports.sources"
                                    :key="row.source"
                                    class="transition-colors duration-150 hover:bg-gray-50"
                                >
                                    <td class="px-6 py-4">
                                        <div class="flex items-center gap-3">
                                            <div
                                                class="inline-block h-4 w-4 rounded-sm bg-gray-100 text-center align-middle"
                                            >
                                                <div
                                                    class="flex h-full w-full items-center justify-center"
                                                >
                                                    <img
                                                        :src="
                                                            row.source ==
                                                            'Ecomail'
                                                                ? 'https://ecomail.cz/images/favicons/favicon-32x32.png'
                                                                : 'https://www.google.com/s2/favicons?domain=' +
                                                                  row.source +
                                                                  '&sz=40'
                                                        "
                                                        :alt="row.source"
                                                    />
                                                </div>
                                            </div>
                                            <a
                                                class="group/domain -m-2 flex cursor-pointer items-center gap-3 rounded-lg p-2 text-left font-medium transition-colors duration-150 hover:bg-blue-50"
                                                :href="
                                                    route(
                                                        'reports.show.source',
                                                        {
                                                            domain: reportedDomain.domain,
                                                            source: row.source,
                                                        },
                                                    )
                                                "
                                                ><span
                                                    class="font-medium text-blue-600 transition-colors duration-150 hover:text-blue-800 group-hover/domain:underline"
                                                    >{{ row.source }}</span
                                                ></a
                                            >
                                        </div>
                                    </td>
                                    <td class="px-6 py-4">
                                        <svg
                                            v-if="
                                                parseInt(
                                                    calculateCompliance(
                                                        row.pass,
                                                        row.fail,
                                                    ),
                                                ) > 80
                                            "
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="lucide lucide-circle-check-big h-4 w-4 text-green-500"
                                            aria-hidden="true"
                                        >
                                            <path
                                                d="M21.801 10A10 10 0 1 1 17 3.335"
                                            ></path>
                                            <path d="m9 11 3 3L22 4"></path>
                                        </svg>
                                        <svg
                                            v-else-if="
                                                parseInt(
                                                    calculateCompliance(
                                                        row.pass,
                                                        row.fail,
                                                    ),
                                                ) > 50
                                            "
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="lucide lucide-circle-alert h-4 w-4 text-yellow-500"
                                            aria-hidden="true"
                                        >
                                            <circle
                                                cx="12"
                                                cy="12"
                                                r="10"
                                            ></circle>
                                            <line
                                                x1="12"
                                                x2="12"
                                                y1="8"
                                                y2="12"
                                            ></line>
                                            <line
                                                x1="12"
                                                x2="12.01"
                                                y1="16"
                                                y2="16"
                                            ></line>
                                        </svg>
                                        <svg
                                            v-else
                                            xmlns="http://www.w3.org/2000/svg"
                                            width="24"
                                            height="24"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            stroke-width="2"
                                            stroke-linecap="round"
                                            stroke-linejoin="round"
                                            class="lucide lucide-circle-x h-4 w-4 text-red-500"
                                            aria-hidden="true"
                                        >
                                            <circle
                                                cx="12"
                                                cy="12"
                                                r="10"
                                            ></circle>
                                            <path d="m15 9-6 6"></path>
                                            <path d="m9 9 6 6"></path>
                                        </svg>
                                    </td>
                                    <td class="px-6 py-4">
                                        <span class="text-gray-900">{{
                                            new Intl.NumberFormat().format(
                                                row.sum,
                                            )
                                        }}</span>
                                    </td>
                                    <td class="px-6 py-4">
                                        <span
                                            class="font-medium text-green-600"
                                            :style="{
                                                color: calculateColorByPercents(
                                                    calculateCompliance(
                                                        row.pass,
                                                        row.fail,
                                                    ),
                                                ),
                                            }"
                                            >{{
                                                calculateCompliance(
                                                    row.pass,
                                                    row.fail,
                                                )
                                            }}</span
                                        >
                                    </td>
                                    <td class="px-6 py-4">
                                        <span
                                            class="font-medium text-yellow-600"
                                            :style="{
                                                color: calculateColorByPercents(
                                                    calculateCompliance(
                                                        row.spf_valid,
                                                        row.spf_invalid,
                                                    ),
                                                ),
                                            }"
                                            >{{
                                                calculateCompliance(
                                                    row.spf_valid,
                                                    row.spf_invalid,
                                                )
                                            }}</span
                                        >
                                    </td>
                                    <td class="px-6 py-4">
                                        <span
                                            class="font-medium text-green-600"
                                            :style="{
                                                color: calculateColorByPercents(
                                                    calculateCompliance(
                                                        row.dkim_valid,
                                                        row.dkim_invalid,
                                                    ),
                                                ),
                                            }"
                                            >{{
                                                calculateCompliance(
                                                    row.dkim_valid,
                                                    row.dkim_invalid,
                                                )
                                            }}</span
                                        >
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </template>
        </div>
    </div>

    <UpgradeRequiredModal
        :opened="showUpgradeModal"
        title="Scale Plan Required"
        description="Custom date range selection requires a Scale plan subscription. Upgrade your subscription to access advanced filtering features."
        @close="closeUpgradeModal"
    />
</template>

<script lang="ts" setup>
import route from "ziggy-js";
import type { PropType } from "vue";
import StatsChart from "@/Pages/Reports/components/StatsChart.vue";
import DateRangePicker from "@/Pages/Reports/components/DateRangePicker.vue";
import UpgradeRequiredModal from "@/components/Atomic/Modals/UpgradeRequiredModal.vue";
import UpgradeRequired from "@/components/Atomic/Miscellaneous/UpgradeRequired.vue";
import useDateRangeAccess from "@/composables/useDateRangeAccess";
import useReportsAccess from "@/composables/useReportsAccess";
import { ref, computed } from "vue";
import { parseDate } from "@internationalized/date";
import type { DateRange } from "reka-ui";
import { router } from "@inertiajs/vue3";

const {
    hasDateRangeAccess,
    showUpgradeModal,
    checkAccessOrShowModal,
    closeUpgradeModal,
} = useDateRangeAccess();

const { hasReportsAccess } = useReportsAccess();

interface ReportSource {
    source: string;
    pass: number;
    fail: number;
    sum: number;
    spf_valid: number;
    spf_invalid: number;
    dkim_valid: number;
    dkim_invalid: number;
}

interface ReportsData {
    total: {
        pass: number;
        fail: number;
    };
    sources: Record<string, ReportSource>;
    chartData: Array<{
        day: string;
        pass: number;
        fail: number;
    }>;
}

interface ReportedDomain {
    domain: string;
    dmarcPolicy: string;
}

const props = defineProps({
    reports: {
        type: Object as PropType<ReportsData>,
        required: true,
    },
    reportedDomain: {
        type: Object as PropType<ReportedDomain>,
        required: true,
    },
    dateRange: {
        type: Object as PropType<{
            start: string;
            end: string;
        }>,
        required: true,
    },
});

// Initialize date range from backend data
const selectedDateRange = ref<DateRange>({
    start: parseDate(props.dateRange.start),
    end: parseDate(props.dateRange.end),
});

// Handle date range changes when user selects new dates
const handleDateRangeChange = (newRange: DateRange) => {
    // Check if user has access to date range picker
    if (!checkAccessOrShowModal()) {
        return;
    }

    // Skip if range is incomplete
    if (!newRange.start || !newRange.end) {
        return;
    }

    const startDate = newRange.start.toString();
    const endDate = newRange.end.toString();

    // Make the request to update data
    router.get(
        route("reports.show", { domain: props.reportedDomain.domain }),
        {
            start_date: startDate,
            end_date: endDate,
        },
        {
            preserveState: false,
            preserveScroll: false,
        },
    );
};

const mainSender = computed(() => {
    return props.reports.sources[Object.keys(props.reports.sources)[0]];
});

const mainWarning = computed(() => {
    return Object.values(props.reports.sources).find(
        (row) =>
            parseInt(
                calculateCompliance(
                    (row as ReportSource).pass,
                    (row as ReportSource).fail,
                ),
            ) < 80,
    ) as ReportSource | undefined;
});

const calculateCompliance = (pass: number, fail: number): string => {
    return pass + fail > 0
        ? Math.floor((pass / (pass + fail)) * 100) + "%"
        : 0 + "%";
};

const calculateColorByPercents = (percents: string): string => {
    var percentNum = parseInt(percents.replace("%", ""));

    if (percentNum > 75) {
        return "#15803D";
    }

    if (percentNum > 50) {
        return "#CA8A04";
    }

    return "#B91C1C";
};
</script>
